import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';
import PageWrapper from '../components/PageWrapper';
import ContentModal from '../components/ContentModal';
import api from '../services/api';
import SafeImage from '../components/SafeImage';
import '../styles/pages/LabStories.css';

const LabStories = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchProjects();

    return () => {
      setLoading(true);
    };
  }, [location.pathname]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.getProjects();

      if (response.success && response.data) {
        setProjects(response.data);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (project) => {
    setSelectedProject(project);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
    document.body.style.overflow = 'unset';
  };

  const truncateText = (text, maxLength) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim();
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <PageWrapper>
      <SEO
        title="Lab Stories | Supernova Lab"
        description="We turn evidence into narrative fuel. Through films, podcasts, essays and visual series, we document what transformation feels like"
        keywords="stories, projects, case studies, media, documentation, Barbados"
      />

      <main className="share-page">
        <motion.section
          className="share-header-section"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <div className="main-share-badge">Share</div>
            <h1 className="share-main-title">
              <span className="animated-title-primary">Lab</span>{' '}
              <span className="text-gradient-dark">Stories</span>
            </h1>

            <p className="share-description">
              We turn evidence into narrative fuel. Through films, podcasts,<br />
              essays and visual series, we document what transformation<br />
              feels like and invite you into the process.
            </p>
          </div>
        </motion.section>

        <section className="studio-section">
          <div className="container">
            <h2 className="share-section-title mb-5">The Studio</h2>

            {loading ? (
              <div className="text-center py-5">
                <i className="fas fa-spinner fa-spin fa-3x"></i>
                <p className="mt-3">Loading stories...</p>
              </div>
            ) : projects.length > 0 ? (
              <motion.div
                className="row g-4"
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {projects.map((project) => (
                  <motion.div key={project.id} className="col-lg-4 col-md-6" variants={itemVariants}>
                    <div className="studio-project-card">
                      <div className="studio-project-image">
                        <SafeImage
                          src={project.image}
                          alt={project.title}
                          className="studio-project-image-img"
                          width={800}
                          height={600}
                          placeholderText="Project"
                        />
                      </div>
                      <div className="studio-project-content">
                        <h3 className="studio-project-title">{project.title}</h3>
                        <p className="studio-project-description">
                          {project.description.length > 120 
                            ? truncateText(project.description, 120)
                            : project.description
                          }
                          {project.description.length > 120 && (
                            <>
                              ...{' '}
                              <span 
                                className="read-more-text"
                                onClick={() => openModal(project)}
                              >
                                Read More
                              </span>
                            </>
                          )}
                        </p>
                        <p className="studio-project-metric">{project.metric}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted">No stories available at the moment</p>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Content Modal */}
      <ContentModal
        isOpen={isModalOpen}
        onClose={closeModal}
        content={selectedProject}
        type="project"
      />
    </PageWrapper>
  );
};

export default LabStories;